import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LookupSearchField from './LookupSearchField';
import LookupResultCard from '../../pages/order-management/components/LookupResultCard';
import quoteService from '../../services/quote.service';
import type { LocationLookupRow } from '../../pages/order-management/fixtures';

/**
 * Reusable enriched Location picker: search cms.locations (name / recno) and store the
 * location recno, showing the name. Built on the shared <LookupSearchField> + enriched
 * <LookupResultCard> (address / city / state), the same look as the Bill To / Customer
 * pickers. Lookup-only (pick an existing location — no create). Used by the SCAC Locations
 * drawer for the Rail Port / CY location fields.
 */
export interface LocationValue {
  recno: number; // 0 = unset
  name: string;
}

const EMPTY: LocationValue = { recno: 0, name: '' };

interface Props {
  value: LocationValue;
  onChange: (v: LocationValue) => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  /** Compact table-cell mode: suppress the field label (column header rendered once). */
  hideLabel?: boolean;
  /** Compact table-cell mode: suppress the "#recno / No location selected" status line. */
  hideStatus?: boolean;
  /** Orange-highlight the input — the stored reference is deleted or no longer
   *  exists (same styling the plain selects use for an unknown value). */
  invalid?: boolean;
}

export const LocationLookupField: React.FC<Props> = ({ value, onChange, label, disabled, required, hideLabel, hideStatus, invalid }) => (
  <div>
    <LookupSearchField<LocationLookupRow>
      invalid={invalid}
      label={label}
      hideLabel={hideLabel}
      required={required}
      displayValue={value.name}
      onType={(text) => onChange({ recno: 0, name: text })}
      onSelect={(r) => onChange({ recno: r.locationcode, name: r.lname })}
      search={(q) => quoteService.lookupLocation(q)}
      renderItem={(r) => (
        <LookupResultCard
          name={r.lname}
          code={r.locationcode}
          address1={r.address1}
          address2={r.address2}
          city={r.city}
          state={r.state}
          zip={r.zip}
        />
      )}
      itemValue={(r) => String(r.locationcode)}
      itemLabel={(r) => r.lname}
      disabled={disabled}
      placeholder="Search locations by name or #…"
    />
    {!hideStatus && (
    <div className="mt-1.5 flex items-center gap-3 text-xs">
      {value.recno > 0 ? (
        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
          #{value.recno}
          {!disabled && (
            <button type="button" onClick={() => onChange(EMPTY)} title="Clear"
              className="ml-1 text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ) : (
        <span className="text-muted">No location selected</span>
      )}
    </div>
    )}
  </div>
);

export default LocationLookupField;
